import React, { useCallback, useEffect, useMemo, useState } from 'react';

import { cn } from '@easydatasolution/config-tailwind/utils';
import {
	NavigationMenu,
	NavigationMenuContent,
	NavigationMenuItem,
	NavigationMenuLink,
	NavigationMenuList,
	NavigationMenuTrigger,
	navigationMenuTriggerStyle,
} from '@easydatasolution/ui-react/components/ui/navigation-menu';
import { FaChevronRight } from '@react-icons/all-files/fa/FaChevronRight';
import { motion, useScroll, useTransform } from 'framer-motion';
import { Link } from 'gatsby';
import { useMeasure } from 'react-use';

import { useCmsData } from '../../../hooks/use-cms-data';
import { useDirectionalScrollProgress } from '../../../hooks/use-directional-scroll-progress';
import { useSettings } from '../../../hooks/use-settings';
import { Url } from '../../../misc/enums';
import { createId } from '../../../misc/utilities';
import { Logo } from '../../Logo/Logo';
import { CartIcon } from '../CartIcon/CartIcon';

const scrollInputRange = [0, 80];

interface NavigationDesktopProps {
	className?: string;
}

export const NavigationDesktop: React.FC<NavigationDesktopProps> = ({ className }) => {
	const [isScrolled, setIsScrolled] = useState(false);

	const [navRef, { height: headerHeight }] = useMeasure<HTMLDivElement>();
	const pixelsScrolled = useDirectionalScrollProgress({
		inputRange: scrollInputRange,
		threshold: { down: headerHeight },
	});
	let { scrollY } = useScroll();
	const barsOpacity = useTransform(pixelsScrolled, scrollInputRange, [1, 0.6]);
	const barsTranslateY = useTransform(pixelsScrolled, scrollInputRange, [0, -(headerHeight + 40)]);
	const logoScale = useTransform(scrollY, scrollInputRange, [1.6, 1]);
	const paddingX = useTransform(scrollY, scrollInputRange, [0, 32]);
	const mr = useTransform(scrollY, scrollInputRange, [96, 16]);

	useEffect(() => {
		const abortController = new AbortController();
		window.addEventListener(
			'scroll',
			() => {
				window.scrollY > 60 ? setIsScrolled(true) : setIsScrolled(false);
			},
			{ signal: abortController.signal },
		);

		return () => abortController.abort();
	}, []);

	return (
		<motion.nav
			className={cn([
				'flex items-center justify-between rounded-lg py-1 pr-8 text-secondary-700',
				isScrolled && 'bg-white shadow-lg',
				className,
			])}
			style={{
				opacity: barsOpacity,
				translateY: barsTranslateY,
				paddingLeft: paddingX,
				paddingRight: paddingX,
			}}
			ref={navRef}
		>
			<NavigationMenu>
				<NavigationMenuList className="flex flex-row items-center">
					<NavigationMenuItem asChild>
						<motion.li style={{ marginRight: mr }}>
							<NavigationMenuLink asChild>
								<motion.div style={{ scale: logoScale, transformOrigin: 'left center' }}>
									<Logo className="h-auto w-[130px] pt-1" />
								</motion.div>
							</NavigationMenuLink>
						</motion.li>
					</NavigationMenuItem>

					<NavigationMenuItem>
						<NavigationMenuTrigger>Digitalisierung</NavigationMenuTrigger>
						<NavigationDropdownDigitalization />
					</NavigationMenuItem>

					<NavigationMenuItem>
						<NavigationMenuTrigger>Kurse</NavigationMenuTrigger>
						<NavigationDropdownCourse />
					</NavigationMenuItem>
				</NavigationMenuList>
			</NavigationMenu>

			<NavigationMenu className="ml-auto">
				<NavigationMenuList className="flex flex-row space-x-5">
					<NavigationMenuItem>
						<NavigationMenuLink asChild>
							<Link to={`/${Url.ABOUT}/`} className={navigationMenuTriggerStyle()}>
								Über uns
							</Link>
						</NavigationMenuLink>
					</NavigationMenuItem>
					<NavigationMenuItem>
						<NavigationMenuLink asChild>
							<Link to={`/${Url.REFERENCES}/`} className={navigationMenuTriggerStyle()}>
								Referenzen
							</Link>
						</NavigationMenuLink>
					</NavigationMenuItem>
					<NavigationMenuItem>
						<NavigationMenuLink asChild>
							<Link to={`/${Url.CONTACT}/`} className={navigationMenuTriggerStyle()}>
								Kontakt
							</Link>
						</NavigationMenuLink>
					</NavigationMenuItem>
					<NavigationMenuItem>
						<NavigationMenuLink asChild>
							<Link to={`/${Url.CART}/`} className={navigationMenuTriggerStyle()}>
								Warenkorb
								<CartIcon className="ml-2" />
							</Link>
						</NavigationMenuLink>
					</NavigationMenuItem>
				</NavigationMenuList>
			</NavigationMenu>
		</motion.nav>
	);
};

function NavigationDropdownDigitalization() {
	const { digitalizationPages } = useCmsData();
	const { digitalizationOrder } = useSettings();

	const dropdownCells = digitalizationOrder.entries.map(
		({ digitalizationParent, digitalizationChildren }) => {
			const { frontmatter: digitalization } = digitalizationPages.find(
				({ node: { frontmatter: digitalizationPage } }) =>
					digitalizationPage.uuid === digitalizationParent,
			).node;

			const children = digitalizationChildren.map((digitalizationChildUuid) => {
				return digitalizationPages.find(
					({ node: { frontmatter: digitalizationPage } }) =>
						digitalizationPage.uuid === digitalizationChildUuid,
				).node;
			});

			return (
				<li key={digitalization.slug}>
					<ParentLink
						to={`/${Url.DIGITALIZATION}/${digitalization.slug}/`}
						title={digitalization.navigationTitle ?? digitalization.title}
						className="px-2"
					/>
					{children.length > 0 && (
						<>
							<hr className="bg-slat-200 mt-2 h-1.5 w-full" />
							<ul className="flex flex-col space-y-2">
								{children.map(({ frontmatter: digitalizationPage }) => {
									const childTo = `/${Url.DIGITALIZATION}/${digitalization.slug}/${digitalizationPage.slug}/`;
									return (
										<li key={childTo} className="px-2">
											<ChildLink
												title={digitalizationPage.navigationTitle ?? digitalizationPage.title}
												to={childTo}
											/>
										</li>
									);
								})}
							</ul>
						</>
					)}
				</li>
			);
		},
	);

	return (
		<NavigationMenuContent>
			<ul className="grid w-[850px] grid-cols-4 gap-x-12 gap-y-8 p-4">{dropdownCells}</ul>
		</NavigationMenuContent>
	);
}

function NavigationDropdownCourse() {
	const { courseTypes, courses, firmenschulungenPage } = useCmsData();
	const { coursesOrder } = useSettings();

	const { distinctCourseTypes, consolidatedCourseTypes } = useMemo(() => {
		const distinct: Array<(typeof coursesOrder.entries)[number]> = [];
		const consolidated: Array<(typeof coursesOrder.entries)[number]> = [];
		coursesOrder.entries.forEach(({ courseType }, index) => {
			if (coursesOrder.otherCourseTypes.includes(courseType)) {
				consolidated.push(coursesOrder.entries[index]);
			} else {
				distinct.push(coursesOrder.entries[index]);
			}
		});
		return { distinctCourseTypes: distinct, consolidatedCourseTypes: consolidated };
	}, [coursesOrder]);

	const getCourseTypesByUuids = useCallback(
		(uuids: string[]) => {
			const allCourseTypesFrontmatter = courseTypes.map(({ node: { frontmatter } }) => frontmatter);
			return uuids.map((uuid) => allCourseTypesFrontmatter.find((ctf) => ctf.uuid === uuid));
		},
		[courseTypes],
	);

	const courseTypeEls = useMemo(() => {
		return distinctCourseTypes.map(({ courseType, courses: navCourses }) => {
			const cTypes = getCourseTypesByUuids([courseType]);

			const menuCourseTypes = cTypes.map((ct) => ({
				to: `/${Url.COURSES}/${ct.slug}/`,
				title: ct.title,
				icon: ct.title,
			}));

			const children = navCourses
				.map((courseUuid) =>
					courses.find(({ node: { frontmatter: course } }) => course.uuid === courseUuid),
				)
				.filter(Boolean);

			return menuCourseTypes.map((ct) => (
				<li key={ct.to}>
					<ParentLink to={ct.to} title={ct.title} className="px-2" />
					{children.length > 0 && (
						<>
							<hr className="bg-slat-200 mt-2 h-1.5 w-full" />
							<ul className="flex flex-col space-y-2">
								{children.map(({ node: { frontmatter: course } }) => (
									<li key={course.uuid} className="px-2">
										<ChildLink
											//  some courses repeat the course type in the title; this is not needed
											title={course.menuTitle ?? course.title.replace(`${ct.title} `, '')}
											to={`/${Url.COURSES}/${course.courseType}/${course.slug}/`}
										/>
									</li>
								))}
							</ul>
						</>
					)}
				</li>
			));
		});
	}, [courses, distinctCourseTypes, getCourseTypesByUuids]);

	const consolidatedCourseTypeEls = consolidatedCourseTypes.map(({ courseType }) => {
		const menuCourseTypes = getCourseTypesByUuids([courseType]).map((ct) => ({
			to: `/${Url.COURSES}/${ct.slug}/`,
			title: ct.title,
			icon: ct.title,
		}));

		return menuCourseTypes.map((ct) => (
			<li key={ct.to} className="px-2">
				<ChildLink title={ct.title} to={ct.to} />
			</li>
		));
	});

	return (
		<NavigationMenuContent>
			<ul className="grid w-[900px] grid-cols-3 gap-x-12 gap-y-8 p-4">
				<li>
					<ParentLink to={`/${Url.FIRMENSCHULUNGEN}/`} title="Schulungsangebote" className="px-2" />

					<hr className="bg-slat-200 mt-2 h-1.5 w-full" />
					<ul className="flex flex-col space-y-2">
						{firmenschulungenPage.frontmatter.sections.map((section) => (
							<li className="px-2" key={section.title}>
								<ChildLink
									title={section.title}
									to={`/${Url.FIRMENSCHULUNGEN}/#${createId(section.title)}`}
								/>
							</li>
						))}
					</ul>
				</li>
				{courseTypeEls}
				{consolidatedCourseTypes.length > 0 && (
					<li className="col-span-2">
						<div className="col-span-2">
							<p className="px-2 font-bold text-secondary-700">Weitere Office Programme</p>
							<hr className="bg-slat-200 mt-2 h-1.5 w-full" />
						</div>
						<ul className="grid grid-cols-2 gap-x-12 gap-y-2">{consolidatedCourseTypeEls}</ul>
					</li>
				)}
			</ul>
		</NavigationMenuContent>
	);
}

type ParentLinkProps = {
	title: string;
	to: string;
	className?: string;
};

function ParentLink({ title, to, className }: ParentLinkProps) {
	return (
		<NavigationMenuLink asChild>
			<Link
				to={to}
				className={cn(
					'space-between flex flex-row items-center font-bold text-secondary-700 hover:text-secondary-800',
					'group',
					className,
				)}
			>
				<span>{title}</span>
				<FaChevronRight className="ml-auto transition-transform duration-200 group-hover:translate-x-1" />
			</Link>
		</NavigationMenuLink>
	);
}

type ChildLinkProps = {
	title: string;
	to: string;
	className?: string;
};

function ChildLink({ title, to, className }: ChildLinkProps) {
	return (
		<NavigationMenuLink asChild>
			<Link to={to} className={cn('text-secondary-700 hover:text-secondary-800', className)}>
				{title}
			</Link>
		</NavigationMenuLink>
	);
}
